import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarIcon, CloseIcon, SendIcon } from './icons.jsx'
import {
  apolloSetup,
  localInputToIso,
  nextMorningLocal,
  sendViaApollo,
  senderMailbox,
  whenLabel,
} from '../lib/apolloSend.js'
import { logEvent } from '../lib/events.js'
import { showToast } from '../lib/toast.js'

// The one door every Apollo send goes through — a single card, a whole batch,
// or the composer. Sending mail is the one irreversible thing this app does,
// so nothing goes until the rep has seen, on one screen: how many emails, to
// whom, FROM WHICH MAILBOX (the Apollo key's user — not necessarily them), and
// when. "Send now" and "Schedule" are the two buttons; there is no default
// that sends on Enter.
//
// `emails` is the server's shape (lib/apolloSend.js#sendViaApollo). Rows
// without a recipient address are listed as skipped, never sent.
const BTN =
  'inline-flex items-center gap-x-1.5 rounded-[9px] px-[14px] py-[9px] text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60'

export default function ApolloSendDialog({ emails, title = 'Send via Apollo', onClose, onSent, layerClass = 'z-[100]' }) {
  const [setup, setSetup] = useState(null) // null = checking
  const [mode, setMode] = useState('now') // 'now' | 'schedule'
  const [when, setWhen] = useState(() => nextMorningLocal())
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const sendingRef = useRef(false)
  sendingRef.current = sending

  const ready = (emails || []).filter((e) => e && e.to && e.subject && (e.body_html || e.body_text))
  const skipped = (emails || []).length - ready.length
  const mailbox = senderMailbox(setup)

  useEffect(() => {
    let alive = true
    apolloSetup().then((s) => alive && setSetup(s))
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && !sendingRef.current) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const scheduleIso = mode === 'schedule' ? localInputToIso(when) : ''
  const scheduleProblem =
    mode !== 'schedule'
      ? ''
      : !scheduleIso
        ? 'Pick a date and time.'
        : new Date(scheduleIso).getTime() < Date.now() - 60_000
          ? 'That time has already passed.'
          : new Date(scheduleIso).getTime() > Date.now() + 30 * 86_400_000
            ? 'Schedule at most 30 days ahead.'
            : ''
  const blocked = !setup?.ok || !mailbox || !ready.length || Boolean(scheduleProblem) || sending

  async function go() {
    if (blocked) return
    setSending(true)
    setError('')
    // ONE SIGNATURE. A card shows the rep's signature under the sign-off; an
    // Apollo mailbox with a signature of its own appends that one too. When
    // it does, the card's unsigned body goes instead.
    const payload = ready.map(({ unsigned, ...e }) =>
      mailbox?.has_signature && unsigned && (unsigned.body_text || unsigned.body_html) ? { ...e, ...unsigned } : e,
    )
    try {
      const res = await sendViaApollo({ emails: payload, sendAt: scheduleIso || null })
      const queued = res.sends?.length || 0
      const refused = res.refused?.length || 0
      logEvent(scheduleIso ? 'apollo_email_scheduled' : 'apollo_email_sent', {
        detail: `${scheduleIso ? 'Scheduled' : 'Sent'} ${queued} email${queued === 1 ? '' : 's'} via Apollo${
          scheduleIso ? ` for ${whenLabel(scheduleIso)}` : ''
        }`,
        data: { count: queued, refused, batch: res.batch_id, due_at: res.due_at, from: mailbox?.email || '' },
      })
      showToast({
        tone: refused && !queued ? 'warn' : 'success',
        title: queued
          ? scheduleIso
            ? `Scheduled ${queued} email${queued === 1 ? '' : 's'}`
            : `Sending ${queued} email${queued === 1 ? '' : 's'}`
          : 'Nothing new to send',
        body: [
          queued ? (scheduleIso ? `Goes out ${whenLabel(scheduleIso)} from ${mailbox.email}.` : `From ${mailbox.email} through Apollo.`) : '',
          refused ? `${refused} ${refused === 1 ? 'was' : 'were'} already queued or sent, and won’t go twice.` : '',
        ]
          .filter(Boolean)
          .join(' '),
        duration: 6000,
      })
      onSent?.(res)
      onClose()
    } catch (err) {
      setError(err?.status === 401 ? 'Your session expired — sign in again and resend.' : err?.message || 'Apollo didn’t take the batch.')
    } finally {
      setSending(false)
    }
  }

  // Portaled to <body>: the cards live inside the chat's sliding two-pane
  // track, whose transform makes it the containing block for `fixed` — a
  // dialog rendered in place was positioned and clipped inside the track.
  return createPortal(
    <div
      className={`fixed inset-0 ${layerClass} flex items-center justify-center bg-[rgba(0,0,0,0.5)] p-6 max-md:p-3`}
      onClick={() => {
        if (!sending) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[85vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[16px] border border-line bg-surface shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-x-3 [border-bottom:1px_solid_var(--border)] px-5 py-4">
          <h3 className="m-0 flex items-center gap-x-2 text-[16px] font-semibold text-ink">
            <SendIcon className="h-[18px] w-[18px] text-dim" />
            {title}
          </h3>
          <button
            type="button"
            className="grid h-8 w-8 place-items-center rounded-[8px] text-faint transition-colors hover:bg-panel hover:text-ink"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {/* FROM — named before anything else, because it may not be you. */}
          <div className="rounded-[10px] border border-line bg-panel px-3.5 py-3 text-[13px] leading-[1.5]">
            {setup === null ? (
              <span className="text-dim">Checking Apollo…</span>
            ) : setup.ok && mailbox ? (
              <>
                <span className="text-dim">From </span>
                <span className="font-semibold text-ink">{mailbox.email}</span>
                <span className="text-dim">
                  {' '}
                  — the mailbox of {setup.sender?.name || setup.sender?.email || 'the Apollo key’s user'} in Apollo
                  {mailbox.has_signature ? ', which adds its own signature (the card’s is left off)' : ''}. Apollo logs each
                  email to HubSpot when “Push emails” is on in its HubSpot settings.
                </span>
              </>
            ) : (
              <span className="text-danger">{setup.error || 'Apollo isn’t set up for sending yet.'}</span>
            )}
          </div>

          <div className="mt-4">
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-[12px] font-semibold text-dim">
                {ready.length} email{ready.length === 1 ? '' : 's'}
              </span>
              {skipped > 0 && (
                <span className="text-[11.5px] text-faint">
                  {skipped} without an address or a draft {skipped === 1 ? 'is' : 'are'} left out
                </span>
              )}
            </div>
            <ul className="m-0 max-h-[220px] list-none overflow-y-auto rounded-[10px] border border-line p-0">
              {ready.map((e, i) => (
                <li
                  key={`${e.draft_key || e.to}-${i}`}
                  className="flex flex-col gap-y-0.5 px-3.5 py-2.5 [border-bottom:1px_solid_var(--border)] last:[border-bottom:none]"
                >
                  <span className="truncate text-[13px] text-ink">
                    <span className="font-semibold">{e.to_name || e.to}</span>
                    {e.to_name && <span className="text-dim"> · {e.to}</span>}
                    {e.company && <span className="text-faint"> · {e.company}</span>}
                  </span>
                  <span className="truncate text-[12.5px] text-dim">{e.subject}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" aria-label="When to send">
            {[
              ['now', 'Send now'],
              ['schedule', 'Schedule'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mode === value}
                onClick={() => setMode(value)}
                className={`rounded-[10px] border px-4 py-2 text-[13px] font-semibold transition-colors ${
                  mode === value ? 'border-primary bg-primary-tint text-primary-dark' : 'border-line bg-surface text-dim hover:bg-panel'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {mode === 'schedule' && (
            <label className="mt-3 flex flex-col gap-y-1.5 text-[12px] font-semibold text-dim">
              Send at (your time)
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="w-full max-w-[260px] rounded-[9px] border border-line bg-panel px-3 py-[9px] text-[14px] font-normal text-ink focus:border-primary focus:outline-none"
              />
              <span className="text-[11.5px] font-normal text-faint">
                {scheduleProblem || `Goes out ${whenLabel(scheduleIso)}. You can cancel it on the card until then.`}
              </span>
            </label>
          )}

          {error && (
            <div className="mt-3 rounded-[10px] bg-surface px-3.5 py-3 text-[13px] leading-[1.5] text-danger [border:1px_solid_var(--border)]">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-x-2 [border-top:1px_solid_var(--border)] px-5 py-3.5">
          <button
            type="button"
            disabled={sending}
            className={`${BTN} border border-line bg-surface text-dim hover:bg-panel hover:text-ink`}
            onClick={onClose}
          >
            Cancel
          </button>
          <button type="button" disabled={blocked} className={`${BTN} bg-primary text-white hover:bg-primary-dark`} onClick={go}>
            {mode === 'schedule' ? <CalendarIcon className="h-[14px] w-[14px]" /> : <SendIcon className="h-[14px] w-[14px]" />}
            {sending
              ? 'Working…'
              : mode === 'schedule'
                ? `Schedule ${ready.length}`
                : `Send ${ready.length} now`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
